import { useRouter } from 'next/router';
import { useAchievementTypes, useDateNow, useReduxState } from 'hooks';
import {
  Badge,
  DisplayAttachments,
  DisplayFormattedDate,
  FlexContainer,
  LinkButton,
  TextWithLineBreaks,
  User,
  Users
} from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AttributeDisplayValue } from 'components/common/customAttributes';
import { IconWithNumber } from 'components/common/icons';
import {
  AchievementUserStatus,
  AchievementRenderAddedBy,
  AchievementUsersActions,
  AchievementUsersDetails,
  AchievementGroupsOfUsersListActions
} from '.';

import type { GetAchievementUsersRes } from 'shared-types/responses';
import type { AchievementUsersView } from './AchievementUsersSubHeader';

interface Props {
  isSupervisorPage?: boolean;
  supervisedUserIds?: number[];
  users: GetAchievementUsersRes['users'];
  updateUsers: () => Promise<void>;
  view: AchievementUsersView;
}

const AchievementUsersTable = ({
  isSupervisorPage = false,
  supervisedUserIds,
  users,
  updateUsers,
  view
}: Props) => {
  const { dateNow: now } = useDateNow();

  const {
    language,
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const router = useRouter();

  const { hasExpiration, hasStartedAt, isGroupAchievement } =
    useAchievementTypes({
      type: achievement?.type
    });

  const handleFocusUser = (userId: number) => {
    router.push(
      { query: { ...router.query, focusedUserId: userId } },
      undefined,
      { shallow: true }
    );
  };

  const isShowingGroupCompletions =
    isGroupAchievement && Boolean(router.query.focusedUserId);

  return (
    <BoxOverflowWrapper>
      <BoxTable>
        <BoxTableHeader>
          <BoxTableCell sticky={true}>
            {isShowingGroupCompletions
              ? messages.commonUsers
              : messages.commonUser}
          </BoxTableCell>
          <BoxTableCell>{messages.commonAddedBy}</BoxTableCell>
          <BoxTableCell>{messages.commonStatus}</BoxTableCell>
          {view === 'all-fields' ? (
            <>
              {hasStartedAt ? (
                <BoxTableCell>{messages.commonStartDate}</BoxTableCell>
              ) : null}
              <BoxTableCell>{messages.completionsCompletionDate}</BoxTableCell>
              {hasExpiration ? (
                <BoxTableCell>{messages.commonExpirationDate}</BoxTableCell>
              ) : null}
              <BoxTableCell>{messages.commonComment}</BoxTableCell>
              <BoxTableCell>{messages.commonAttachments}</BoxTableCell>
              {achievement.userAttributes?.map((attribute) => (
                <BoxTableCell key={attribute.id}>
                  {attribute.titleTranslations?.[language] || attribute.title}
                </BoxTableCell>
              ))}
            </>
          ) : null}
          <BoxTableCell></BoxTableCell>
          <BoxTableCell
            shrink={true}
            sticky={true}
            stickyAnchor="right"
          ></BoxTableCell>
        </BoxTableHeader>

        {users.map(
          ({ completions = [], numberOfPreviousCompletions = 0, ...user }) => {
            const completion = completions[0];
            const hasMultipleCompletions = completions.length > 1;
            const hasMultipleSources = completions.some(
              (completion) =>
                JSON.stringify(completion.addedBy) !==
                JSON.stringify(completions[0].addedBy)
            );

            const linkAs = isGroupAchievement
              ? `/admin/achievements/${achievement.id}/group-conversations/${completion?.id}`
              : `/admin/achievements/${achievement.id}/users/${user.id}`;
            const linkHref = isGroupAchievement
              ? '/admin/achievements/[achievementId]/group-conversations/[completionId]'
              : '/admin/achievements/[achievementId]/users/[userId]';

            return (
              <BoxTableRow key={`${user.id}-${completion?.id || 0}`}>
                <BoxTableCell
                  sticky={true}
                  stickyBackgroundColor="white"
                  style={{ minWidth: 220 }}
                >
                  {isShowingGroupCompletions ? (
                    <div
                      onClick={() => router.push(linkHref, linkAs)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Users users={completion?.members} />
                    </div>
                  ) : (
                    <User
                      user={user}
                      {...(hasMultipleCompletions
                        ? { onClick: () => handleFocusUser(user.id) }
                        : completion
                        ? { enableProfileLink: true, linkAs, linkHref }
                        : {})}
                    />
                  )}
                </BoxTableCell>

                <BoxTableCell style={{ minWidth: 220 }}>
                  {hasMultipleSources ? (
                    <Badge
                      isRounded={false}
                      text={messages.commonMultipleSources}
                    />
                  ) : (
                    <AchievementRenderAddedBy
                      addedBy={completion?.addedBy}
                      enableLink={true}
                    />
                  )}
                </BoxTableCell>

                <BoxTableCell>
                  <FlexContainer style={{ gap: 5 }}>
                    <AchievementUserStatus
                      achievementData={completion}
                      achievementType={achievement.type}
                      numberOfCompletions={completions?.length}
                      numberOfItems={achievement.template.length}
                      showCompletedTextLabel={view === 'all-fields'}
                    />

                    {numberOfPreviousCompletions ? (
                      <LinkButton
                        category="lighter"
                        href={`/admin/achievements/${achievement.id}/users/${user.id}`}
                        size="header"
                        style={{ fontSize: '1.3rem', padding: '4px 6px 4px' }}
                        text={`+${numberOfPreviousCompletions}`}
                        tooltip={messages.completionsPreviousCompletions}
                      />
                    ) : null}
                  </FlexContainer>
                </BoxTableCell>

                {view === 'all-fields' ? (
                  <>
                    {hasStartedAt ? (
                      <BoxTableCell>
                        {!hasMultipleCompletions &&
                        completion?.startedAtDate ? (
                          <DisplayFormattedDate
                            date={completion.startedAtDate}
                          />
                        ) : null}
                      </BoxTableCell>
                    ) : null}

                    <BoxTableCell
                      style={{ color: theme.successStandaloneColor }}
                    >
                      {!hasMultipleCompletions && completion?.completionDate ? (
                        <DisplayFormattedDate
                          date={completion.completionDate}
                        />
                      ) : null}
                    </BoxTableCell>

                    {hasExpiration ? (
                      <BoxTableCell
                        style={{
                          color:
                            Date.parse(completion?.expirationDate as string) <
                            now
                              ? theme.dangerColor
                              : theme.textColorLight
                        }}
                      >
                        {!hasMultipleCompletions &&
                        completion?.expirationDate ? (
                          <DisplayFormattedDate
                            date={completion.expirationDate}
                          />
                        ) : null}
                      </BoxTableCell>
                    ) : null}

                    <BoxTableCell>
                      {!hasMultipleCompletions && completion?.comments ? (
                        <TextWithLineBreaks
                          text={completion.comments}
                          style={{ fontWeight: 400, minWidth: 300 }}
                        />
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      {!hasMultipleCompletions ? (
                        <DisplayAttachments
                          attachments={completion?.attachments || []}
                          isConfidential={!completion?.canSeeAttachments}
                          modalButtonProps={{
                            category: 'lighter',
                            iconStyle: { fontSize: '1.1rem' },
                            size: 'small',
                            style: {
                              borderRadius: 20,
                              fontSize: '1.2rem',
                              fontWeight: 600,
                              padding: '3px 9px 4px 7px'
                            }
                          }}
                        />
                      ) : null}
                    </BoxTableCell>

                    {achievement.userAttributes?.map((attribute) => (
                      <BoxTableCell
                        key={attribute.id}
                        style={{ minWidth: 160 }}
                      >
                        {hasMultipleCompletions ? null : (
                          <AttributeDisplayValue
                            value={completion?.userAttributes?.[attribute.id]}
                          />
                        )}
                      </BoxTableCell>
                    ))}
                  </>
                ) : null}

                <BoxTableCell
                  shrink={true}
                  valueContainerStyle={{ justifyContent: 'flex-end' }}
                >
                  {hasMultipleCompletions ? null : (
                    <div style={{ display: 'flex', gap: 20 }}>
                      {view === 'default' && completion?.attachments?.length ? (
                        <IconWithNumber
                          icon="paperclip"
                          number={completion.attachments.length}
                          tooltip={messages.commonAttachments}
                        />
                      ) : null}

                      {view === 'default' && completion?.comments ? (
                        <IconWithNumber
                          icon={['far', 'comment']}
                          tooltip={messages.commonComment}
                        />
                      ) : null}

                      <AchievementUsersDetails
                        achievementData={completion}
                        user={user}
                      />
                    </div>
                  )}
                </BoxTableCell>

                <BoxTableCell
                  valueContainerStyle={{ justifyContent: 'flex-end' }}
                  shrink={true}
                  sticky={true}
                  stickyAnchor="right"
                  stickyBackgroundColor="white"
                >
                  {hasMultipleCompletions ? null : isGroupAchievement &&
                    completion ? (
                    <AchievementGroupsOfUsersListActions
                      groupCompletion={{
                        achievementData: completion,
                        members: completion?.members || []
                      }}
                      id={`achievement-users-table-${user.id}-${
                        completion?.id || 0
                      }`}
                      isSupervisorPage={isSupervisorPage}
                      updateGroups={updateUsers}
                    />
                  ) : (
                    <AchievementUsersActions
                      isSupervisorPage={isSupervisorPage}
                      supervisedUserIds={supervisedUserIds}
                      targetUser={{ ...user, achievementData: completion }}
                      updateUsers={updateUsers}
                    />
                  )}
                </BoxTableCell>
              </BoxTableRow>
            );
          }
        )}
      </BoxTable>
    </BoxOverflowWrapper>
  );
};

export default AchievementUsersTable;
